// src/component/PrefectureChat20251222.js
import React, { useEffect, useState } from "react";
import { Chat, Channel, MessageList, Window } from "stream-chat-react";
import { useStreamChatClient } from "../context/StreamChatProvider";
import { getFirestore, doc, setDoc, getDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import "stream-chat-react/dist/css/index.css";
import "../css/streamOverrides.css";
import { TypingIndicator } from "./TypingIndicator";
import CustomMessageInput from "./CustomMessageInput";
import { Box, Typography, IconButton, Tooltip, GlobalStyles } from "@mui/material";
import { logChatPromiseExecution } from "stream-chat";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Badge from "@mui/material/Badge";

import ChannelMembersDrawer from "./ChannelMembersDrawer";

import { MESSAGE_ACTIONS } from "stream-chat-react";
/**
 * 2025/12/22以降：
 * - 新しいチャンネルIDへ切替（全員）
 * - そのチャンネルに後から来た人は、参加以前の履歴を見せない（hide_history）
 */
const CUTOFF_ISO = "2025-12-22T00:00:00+09:00";
const NEW_SUFFIX = "v20251222";

// 今日が cutoff 以降か
function isAfterCutoff() {
  return Date.now() >= new Date(CUTOFF_ISO).getTime();
}

// チャンネルIDをバージョニング（県/地域/全国すべて同じルールでOK）
function resolveChannelId(baseGroupId) {
  if (!baseGroupId) return baseGroupId;
  if (!isAfterCutoff()) return baseGroupId; // それ以前は旧チャンネル
  return `${baseGroupId}_${NEW_SUFFIX}`; // 以降は新チャンネルへ
}

const Chat20251222 = ({ groupId: baseGroupId, region }) => {
  const chatClient = useStreamChatClient();
  const [channel, setChannel] = useState(null);
  const [key, setKey] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const logErrorToFirestore = async (error, additionalInfo = {}) => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      console.error(
        "No authenticated user found. Error not logged to Firestore."
      );
      return;
    }

    try {
      const db = getFirestore();
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);

      const errorDetails = {
        errorMessage: error?.message || String(error),
        errorStack: error?.stack || null,
        ...additionalInfo,
        userId: user.uid,
        userInfo: userDoc.exists() ? userDoc.data() : {},
        userAgent: navigator.userAgent,
        url: window.location.href,
        date: new Date().toISOString(),
      };

      await setDoc(
        doc(db, "errors", `${user.uid}_${Date.now()}`),
        errorDetails
      );
    } catch (firestoreError) {
      console.error("Error logging to Firestore:", firestoreError);
    }
  };

  // userInfo（admin判定用）
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), async (user) => {
      try {
        if (!user) {
          setUserInfo(null);
          return;
        }
        const db = getFirestore();
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) setUserInfo(userDoc.data());
        else setUserInfo(null);
      } catch (e) {
        console.error("Failed to fetch userInfo:", e);
        setUserInfo(null);
      }
    });

    return () => unsub();
  }, []);

  // チャンネル初期化

  useEffect(() => {
    let cancelled = false;
    let chRef = null;

    // チャンネル切替のたびに一旦クリア（旧チャンネル残り防止）
    setChannel(null);

    const initChat = async (userId) => {
      try {
        if (cancelled) return;
        if (!chatClient) return;
        if (!baseGroupId) return;

        const channelId = resolveChannelId(baseGroupId);

        const ch = chatClient.channel("messaging", channelId, {
          name: `${region || channelId} チャット`,
          invite_only: false,
        });
        chRef = ch;

        // 存在確認→なければ作成
        try {
          await ch.query();
        } catch (e) {
          if (e?.response?.status === 404) {
            await ch.create();
          } else {
            throw e;
          }
        }

        if (cancelled) return;

        // 参加判定：成功する環境なら queryMembers が最適
        let membershipKnown = false;
        let isMember = false;

        try {
          const mem = await ch.queryMembers(
            { id: { $eq: userId } },
            { limit: 1 }
          );
          membershipKnown = true;
          isMember = !!(mem?.members && mem.members.length > 0);
        } catch (e) {
          console.warn(
            "queryMembers failed; will addMembers without hide_history for safety:",
            e
          );
        }

        if (cancelled) return;

        if (membershipKnown) {
          if (!isMember) {
            await ch.addMembers([userId], { hide_history: true });
          } else {
            await ch.addMembers([userId]);
          }
        } else {
          // 判定できないなら hide_history を付けない（既存ユーザー保護）
          await ch.addMembers([userId]);
        }

        if (cancelled) return;

        await ch.watch();

        if (!cancelled) setChannel(ch);
      } catch (error) {
        console.error("initChat error:", error);
        await logErrorToFirestore(error, {
          functionName: "initChat",
          baseGroupId,
        });
      }
    };

    const unsubAuth = onAuthStateChanged(getAuth(), (u) => {
      if (cancelled) return;
      if (!u?.uid) return;
      initChat(u.uid);
    });

    return () => {
      cancelled = true;
      unsubAuth();
      try {
        if (chRef?.stopWatching) chRef.stopWatching();
      } catch (e) {
        // noop
      }
      chRef = null;
    };
  }, [chatClient, baseGroupId, region]);


  // useEffect(() => {
  //   if (!chatClient) return;
  //   if (!channel) return;
  
  //   const myId = chatClient.userID;
  
  //   console.log("client user:", chatClient.user);
  //   console.log("client user role:", chatClient.user?.role);
  
  //   console.log("myId:", myId);
  //   console.log("member:", channel.state?.members?.[myId]);
  //   console.log("member role:", channel.state?.members?.[myId]?.role);
  
  //   // 追加：Streamのcapabilitiesを確認（ここが一番強い）
  //   console.log("ownCaps:", channel.getCapabilities?.() || "no getCapabilities");
  //   console.log("amIModerator:", channel?.state?.membership?.role); // 出る場合あり
  // }, [chatClient, channel]);
  

  //   useEffect(() => {
  //     let cancelled = false;

  //     const initChat = async () => {
  //       try {
  //         if (!chatClient) return;

  //         const auth = getAuth();
  //         const u = auth.currentUser;
  //         const userId = u ? u.uid : null;

  //         if (!userId) {
  //           console.error("User not authenticated");
  //           return;
  //         }

  //         const channelId = resolveChannelId(baseGroupId);

  //         // “新しい部屋” へ切替（12/22以降は suffix 付き）
  //         const ch = chatClient.channel("messaging", channelId, {
  //           name: `${region || channelId} チャット`,
  //           invite_only: false,
  //           // ※ここで history:false を置いても要件は満たせません（全員が見えなくなる等）
  //           // ※「新規参加者だけ見えない」は hide_history でやる
  //         });

  //         // 存在確認→なければ作成
  //         try {
  //           await ch.query();
  //         } catch (e) {
  //           if (e?.response?.status === 404) {
  //             console.log("Channel does not exist, creating:", channelId);
  //             await ch.create();
  //           } else {
  //             throw e;
  //           }
  //         }

  //         // 重要：メンバー未参加なら「参加以前の履歴を隠す」
  //         // queryMembers は「自分が未参加でも」問い合わせできる想定（権限設定に依存）
  //         let isMember = false;
  //         try {
  //           const mem = await ch.queryMembers({ id: { $eq: userId } }, { limit: 1 });
  //           isMember = !!(mem?.members && mem.members.length > 0);
  //         } catch (e) {
  //           // 権限で queryMembers が落ちる可能性があるため保険
  //           console.warn("queryMembers failed; fallback to addMembers with hide_history:", e);
  //         }

  //         if (!isMember) {
  //           // 新規参加者：過去履歴を見せない
  //           // addMembers の第2引数に hide_history を付けるのが肝
  //           await ch.addMembers([userId], { hide_history: true });
  //         } else {
  //           // 既存参加者：念のため所属だけ保証（既に所属ならno-op）
  //           await ch.addMembers([userId]);
  //         }

  //         await ch.watch();

  //         if (!cancelled) {
  //           setChannel(ch);
  //         }
  //       } catch (error) {
  //         console.error("initChat error:", error);
  //         await logErrorToFirestore(error, { functionName: "initChat", baseGroupId });
  //       }
  //     };

  //     initChat();

  //     return () => {
  //       cancelled = true;
  //     };
  //   }, [chatClient, baseGroupId, region]);

  const overrideSubmitHandler = async (message) => {
    if (
      !message.text?.trim() &&
      (!message.attachments || message.attachments.length === 0)
    ) {
      console.error("空のメッセージや添付ファイルのみは送信できません");
      return;
    }

    try {
      if (channel) {
        const sendMessagePromise = channel.sendMessage(message);
        logChatPromiseExecution(sendMessagePromise, "メッセージ送信");
        setKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error("メッセージ送信失敗:", error);
      await logErrorToFirestore(error, {
        functionName: "overrideSubmitHandler",
      });
    }
  };

  const actions = ["delete", "edit", "react"];
// デフォルト actions から flag だけ抜く

const messageActions = Object.keys(MESSAGE_ACTIONS).filter(
  (a) => !["flag", "mute", "reply"].includes(a)
);

const NoDateSeparator = () => null;


  if (!chatClient || !channel) return <div>Loading chat...</div>;

  return (
    <Box
      sx={{
        borderRadius: 2,
        boxShadow: 3,
        backgroundColor: "#fff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          backgroundColor: "#fafafa",
          borderBottom: "5px solid #ddd",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {region}チャット
        </Typography>

        {!!userInfo?.isAdmin && (
          <Tooltip title="メンバーを見る">
            <Badge
              badgeContent={Object.keys(channel.state.members || {}).length}
              color="secondary"
            >
              <IconButton
                onClick={() => setIsDialogOpen(true)}
                sx={{
                  ml: 2,
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "rotate(15deg)" },
                }}
                aria-label="メンバーを見る"
              >
                <InfoOutlinedIcon color="primary" />
              </IconButton>
            </Badge>
          </Tooltip>
        )}
      </Box>

      {/* チャット本体 */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <Chat client={chatClient}>
          <Channel key={key} channel={channel} >
            <Window>
              <MessageList
                messageActions={messageActions}
                hideNewMessageSeparator
                showUnreadNotificationAlways
                disableDateSeparator
                DateSeparator={NoDateSeparator}
              />

              <Box className="pl-5 py-2 bg-white">
                <TypingIndicator />
              </Box>

              <Box
                sx={{
                  position: { xs: "sticky", md: "static" },
                  bottom: { xs: 64, md: 0 },
                  zIndex: 5,
                  bgcolor: "#fff",
                }}
              >
                <CustomMessageInput
                  overrideSubmitHandler={overrideSubmitHandler}
                />
              </Box>
            </Window>
          </Channel>
        </Chat>
      </Box>

      <ChannelMembersDrawer
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        channel={channel}
        currentUser={userInfo}
      />
    </Box>
  );
};

export default Chat20251222;
